import { SseConexion } from './autoBffClient';
import { BffError } from './bffError';
import {
  adaptPrecios,
  CompaniaLegacy,
  ContextoAdaptacionPrecio,
  ItemPrecioLegacy,
} from './adapters';
import * as bff from './endpoints';
import { guardarCotizacionId, leerSesionCotizacion } from './quoteSession';
import { BffCotizacionSnapshot, BffPlan, Ramo } from './types';

/**
 * Precios por SSE: UNA sola conexión reemplaza al `Promise.all` de una llamada
 * HTTP por compañía. Los planes se entregan por compañía a medida que llegan,
 * ya convertidos al shape viejo, para que la pantalla siga pintando las mismas
 * cards con el mismo estado.
 */

export interface ContextoPrecios {
  /** Compañías del bootstrap, para resolver nombre y emisión habilitada. */
  companias: CompaniaLegacy[];
  /** Logo por código de compañía (la pantalla ya sabe resolverlo). */
  logoDe: (codigoCompania?: string) => string;
  /** Imagen del sello de AFIP que el shape viejo lleva en cada item. */
  afip: string;
  /**
   * Descripciones de cobertura del store. Se cruzan por cobertura y, cuando el
   * BFF las manda con compañía, también por compañía.
   */
  descripcionesDe?: (
    codigoCobertura: string,
    codigoCompania: string
  ) => Array<{ Nombre: string; Descripcion: string; Aclaracion: string | null }>;
  /** Resumen corto de coberturas que muestran las cards. */
  resumenDe?: (
    descripciones: Array<{ Nombre: string; Descripcion: string }>
  ) => string[];
  sumaAseguradaFallback?: number | null;
}

export interface ManejadoresPrecios {
  /** Planes de UNA compañía, listos para concatenar al estado. */
  onCompania: (codigoCompania: string, items: ItemPrecioLegacy[]) => void;
  /** Una compañía falló: la pantalla deja de esperarla. */
  onCompaniaError?: (codigoCompania: string, code: string) => void;
  /**
   * Llegó `started`: ya se conocen las compañías, la suma asegurada de la
   * cotización y hasta cuándo valen estos precios.
   */
  onInicio?: (data: {
    cotizacionId: string;
    companias: string[];
    sumaAsegurada?: number | null;
    vigenciaHasta?: string;
  }) => void;
  /** Terminó el stream (`done`). */
  onFin?: (total: number, degraded: boolean) => void;
  /** Se cayó el stream: la pantalla pide el snapshot para reconciliar. */
  onFalla?: (error: BffError) => void;
}

/**
 * Código de compañía, venga como venga del BFF.
 *
 * El contrato §3.3 NO usa la misma forma en los tres lugares: el evento `result` y cada plan
 * la mandan como objeto (`{codigo, nombre, logoUrl}`) y el evento `error` la manda como
 * string. Tratar el objeto como string rompía el stream entero con
 * `codigoCompania.toLowerCase is not a function`, la pantalla de precios quedaba vacía y caía
 * al snapshot (que además devolvía 404 mientras la cotización seguía en curso). Se absorbe
 * acá, en un solo lugar, en vez de repetir el chequeo en cada llamador.
 */
export const codigoCompaniaDe = (
  compania?: string | { codigo?: string; nombre?: string } | null
): string => {
  if (!compania) return '';
  if (typeof compania === 'string') return compania;
  return compania.codigo ?? '';
};

const contextoDe = (
  ctx: ContextoPrecios,
  compania_: string | { codigo?: string; nombre?: string }
): ContextoAdaptacionPrecio => {
  const codigoCompania = codigoCompaniaDe(compania_);
  const nombreDelEvento =
    typeof compania_ === 'object' && compania_ ? compania_.nombre : undefined;
  const compania = ctx.companias.find(
    (c) => c.codigoCompania?.toLowerCase() === codigoCompania?.toLowerCase()
  );
  return {
    codigoCompania,
    nombreCompania: compania?.nombreCompania || nombreDelEvento || codigoCompania,
    logoCompania: ctx.logoDe(codigoCompania),
    afip: ctx.afip,
    emisionHabilitada: !!compania?.emisionOnlineHabilitada,
    sumaAseguradaFallback: ctx.sumaAseguradaFallback,
  };
};

/** Completa descripciones y resumen del item con lo que sabe la pantalla. */
const enriquecer = (
  items: ItemPrecioLegacy[],
  ctx: ContextoPrecios
): ItemPrecioLegacy[] =>
  items.map((item) => {
    const descripciones = ctx.descripcionesDe
      ? ctx.descripcionesDe(item.codigoCobertura, item.CodigoCompania)
      : item.descripciones;
    return {
      ...item,
      descripciones: descripciones.length ? descripciones : item.descripciones,
      DescResumen: ctx.resumenDe ? ctx.resumenDe(descripciones) : [],
    };
  });

export const adaptarPlanesDeCompania = (
  planes: BffPlan[],
  compania: string | { codigo?: string; nombre?: string },
  ctx: ContextoPrecios
): ItemPrecioLegacy[] =>
  enriquecer(adaptPrecios(planes, contextoDe(ctx, compania)), ctx);

/**
 * Abre el stream de precios. Devuelve la conexión para poder cancelarla.
 * `abrir` es el `POST precios` de siempre; `/retomar` pasa el suyo (el mismo endpoint con
 * `forzar: true` y el token del canje) para recotizar con el intake guardado.
 */
export const escucharPrecios = (
  ramo: Ramo,
  ctx: ContextoPrecios,
  handlers: ManejadoresPrecios,
  companias?: string[],
  abrir: typeof bff.abrirPreciosSse = bff.abrirPreciosSse
): SseConexion =>
  abrir(ramo, companias, {
    onStarted: (data) => {
      guardarCotizacionId(data.cotizacionId);
      handlers.onInicio?.({
        cotizacionId: data.cotizacionId,
        companias: data.companias || [],
        sumaAsegurada: data.sumaAsegurada,
        vigenciaHasta: data.vigenciaHasta,
      });
    },
    onResult: (data) => {
      handlers.onCompania(
        codigoCompaniaDe(data.compania),
        adaptarPlanesDeCompania(data.planes || [], data.compania, ctx)
      );
    },
    onError: (data) => handlers.onCompaniaError?.(data.compania, data.code),
    onDone: (data) => handlers.onFin?.(data.total, data.degraded),
    onFailure: (error) => handlers.onFalla?.(error),
  });

/**
 * Snapshot de la cotización: se usa cuando se pierde el stream o se vuelve a
 * la pantalla. Devuelve todos los planes ya adaptados, agrupados por compañía.
 */
export const cargarSnapshotPrecios = async (
  ramo: Ramo,
  ctx: ContextoPrecios
): Promise<ItemPrecioLegacy[]> => {
  const { cotizacionId } = leerSesionCotizacion();
  if (!cotizacionId) return [];
  return adaptarSnapshot(await bff.getCotizacion(ramo, cotizacionId), ctx);
};

/**
 * Snapshot -> items, agrupados por compañía. La clave es el CÓDIGO, no el objeto `compania` del
 * plan: usar el objeto daba una entrada por plan (las claves de Map se comparan por referencia) y
 * rompía el agrupado por compañía. Lo usa también `/retomar`, que recibe el snapshot en el canje.
 */
export const adaptarSnapshot = (
  snapshot: Pick<BffCotizacionSnapshot, 'planes'>,
  ctx: ContextoPrecios
): ItemPrecioLegacy[] => {
  const porCompania = new Map<string, BffPlan[]>();
  (snapshot.planes || []).forEach((plan) => {
    const codigo = codigoCompaniaDe(plan.compania);
    const lista = porCompania.get(codigo) || [];
    lista.push(plan);
    porCompania.set(codigo, lista);
  });
  const items: ItemPrecioLegacy[] = [];
  porCompania.forEach((planes, compania) => {
    items.push(...adaptarPlanesDeCompania(planes, compania, ctx));
  });
  return items;
};
